import React, { useState } from "react";
import PropTypes, { InferProps } from "prop-types";
import "./RadialBarChart.css";
import RadialBarChartCircle from "./RadialBarChartCircle";
import { IRadialBarChartLegend } from "./IRadialBarChartLegend";

export function RadialBarChart(props: InferProps<typeof RadialBarChart.propTypes>) {
  // State
  const [legend, setLegend] = useState<IRadialBarChartLegend | null>();

  // Functions
  const hasNoPrePostData = function (): boolean {
    if (props.VorangehendeKompetenzstufen_Zyklus1 || props.VorangehendeKompetenzstufen_Zyklus2 || props.VorangehendeKompetenzstufen_Zyklus3) {
      return false;
    }
    if (props.WeiterfuehrendeKompetenzstufen_Zyklus1 || props.WeiterfuehrendeKompetenzstufen_Zyklus2 || props.WeiterfuehrendeKompetenzstufen_Zyklus3) {
      return false;
    }

    return true;
  };

  const showGrundanspruchOnly = props.showGrundanspruchOnly ?? hasNoPrePostData();

  const showLegend = function (zyklus: number, level: string, percentageCore?: number | null, percentagePre?: number | null, percentagePost?: number | null) {
    if (props.onHover) {
      props.onHover(zyklus, level);
    }

    setLegend({ zyklus: zyklus, percentageCore: percentageCore, percentagePre: percentagePre, percentagePost: percentagePost } as IRadialBarChartLegend);
  };

  const hideLegend = function (keepDynamicLegend: boolean) {
    if (!keepDynamicLegend) {
      setLegend(null);
    }

    if (props.onHover) {
      props.onHover(null, null);
    }
  };

  // Render
  return (
    <div className="lp21-radialbarchart" onMouseLeave={() => hideLegend(false)}>
      {legend && (
        <div className="lp21-legend">
          <div className={`lp21-zyklus lp21-zyklus${legend.zyklus}`}>{legend.zyklus}. Zyklus</div>
          {showGrundanspruchOnly && <div className="lp21-values">{legend.percentageCore}%</div>}
          {!showGrundanspruchOnly && (
            <>
              <div className="lp21-values">
                <span title="Grundanspruch">{legend.percentageCore}%</span>
              </div>
              <div className="lp21-values lp21-values-detailed">
                <span title="Vorangehende Kompetenzstufen">{legend.percentagePre}%</span>
                <span title="Weiterführende Kompetenzstufen">{legend.percentagePost}%</span>
              </div>
            </>
          )}
        </div>
      )}

      <svg viewBox="0 0 100 100" width="100%" height={props.Height ? props.Height + "px" : "100%"} xmlns="http://www.w3.org/2000/svg">
        {/* Zyklus 1 */}
        <RadialBarChartCircle
          showCoreOnly={showGrundanspruchOnly}
          Radius={28}
          ColorPre="rgba(227, 155, 45, 0.3)"
          ColorCore="rgb(227, 155, 45)"
          ColorPost="rgb(181, 124, 36)"
          PercentagePre={props.VorangehendeKompetenzstufen_Zyklus1 ?? 0}
          PercentageCore={props.Grundanspruch_Zyklus1 ?? 0}
          PercentagePost={props.WeiterfuehrendeKompetenzstufen_Zyklus1 ?? 0}
          onEnter={(level: string) => showLegend(1, level, props.Grundanspruch_Zyklus1, props.VorangehendeKompetenzstufen_Zyklus1, props.WeiterfuehrendeKompetenzstufen_Zyklus1)}
          onLeave={() => hideLegend(true)}
        />
        {/* Zyklus 2 */}
        <RadialBarChartCircle
          showCoreOnly={showGrundanspruchOnly}
          Radius={34}
          ColorPre="rgba(47, 143, 206, 0.3)"
          ColorCore="rgb(47, 143, 206)"
          ColorPost="rgb(38, 114, 164)"
          PercentagePre={props.VorangehendeKompetenzstufen_Zyklus2 ?? 0}
          PercentageCore={props.Grundanspruch_Zyklus2 ?? 0}
          PercentagePost={props.WeiterfuehrendeKompetenzstufen_Zyklus2 ?? 0}
          onEnter={(level: string) => showLegend(2, level, props.Grundanspruch_Zyklus2, props.VorangehendeKompetenzstufen_Zyklus2, props.WeiterfuehrendeKompetenzstufen_Zyklus2)}
          onLeave={() => hideLegend(true)}
        />
        {/* Zyklus 3 */}
        <RadialBarChartCircle
          showCoreOnly={showGrundanspruchOnly}
          Radius={40}
          ColorPre="rgba(151, 201, 65, 0.5)"
          ColorCore="rgb(151, 201, 65)"
          ColorPost="rgb(120, 160, 52)"
          PercentagePre={props.VorangehendeKompetenzstufen_Zyklus3 ?? 0}
          PercentageCore={props.Grundanspruch_Zyklus3 ?? 0}
          PercentagePost={props.WeiterfuehrendeKompetenzstufen_Zyklus3 ?? 0}
          onEnter={(level: string) => showLegend(3, level, props.Grundanspruch_Zyklus3, props.VorangehendeKompetenzstufen_Zyklus3, props.WeiterfuehrendeKompetenzstufen_Zyklus3)}
          onLeave={() => hideLegend(true)}
        />
      </svg>
    </div>
  );
}

RadialBarChart.propTypes = {
  Height: PropTypes.number,
  showGrundanspruchOnly: PropTypes.bool,

  Grundanspruch_Zyklus1: PropTypes.number,
  Grundanspruch_Zyklus2: PropTypes.number,
  Grundanspruch_Zyklus3: PropTypes.number,

  VorangehendeKompetenzstufen_Zyklus1: PropTypes.number,
  VorangehendeKompetenzstufen_Zyklus2: PropTypes.number,
  VorangehendeKompetenzstufen_Zyklus3: PropTypes.number,

  WeiterfuehrendeKompetenzstufen_Zyklus1: PropTypes.number,
  WeiterfuehrendeKompetenzstufen_Zyklus2: PropTypes.number,
  WeiterfuehrendeKompetenzstufen_Zyklus3: PropTypes.number,

  onHover: PropTypes.func,
};

export default RadialBarChart;
